import type {
  RyftAccountTransactionsListResponseItemType,
  RyftAccountPendingPayoutType,
} from "@sellar-io/api-v2/types";
import type { TableSimpleColumn } from "@sellar-io/components";
import { formatCurrency } from "@sellar-io/common";
import {
  Button,
  Card,
  CardContent,
  Icon,
  TooltipContainer,
  Spinner,
  TablePaginated,
  TableSimple,
  createSellarColumnHelper,
} from "@sellar-io/components";
import type { PaginationState } from "@tanstack/react-table";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useRyftManagementPendingPayouts } from "~/hooks/api/v2/ryftManagement/useRyftManagementPendingPayouts";
import { useState, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Alert } from "~/components/alert/Alert";
import {
  Dialog,
  DialogContent,
  DialogNavigation,
} from "~/components/modal/Dialog";
import { ParentLink } from "~/components/navigation/ParentLink";
import { ORDER_DETAILS } from "~/constants/routes";
import { useRyftManagementLedgerBalance } from "~/hooks/api/v2/ryftManagement/useRyftManagementLedgerBalance";
import { useRyftManagementLedgerTransactions } from "~/hooks/api/v2/ryftManagement/useRyftManagementLedgerTransactions";
import { useRyftNonHostedDashboardAccount } from "./components/RyftNonHostedDashboardAccountProvider.shared";

dayjs.extend(utc);
dayjs.extend(timezone);

type Transaction = RyftAccountTransactionsListResponseItemType;

const activityLabels: Record<Transaction["type"], string> = {
  capture: "Card payment",
  refund: "Refund for card payment",
  fundsTransfer: "Sellar Credit payment",
  bankPayout: "Sent to your bank",
  bankPayoutReversal: "Back in your balance",
};

const tableAmountLabels: Record<Transaction["type"], string | null> = {
  capture: "After fees",
  refund: null,
  fundsTransfer: "After fees",
  bankPayout: null,
  bankPayoutReversal: null,
};

const getAfterFeesAmount = (transaction: Transaction) => {
  if (transaction.type !== "capture") {
    return transaction.amount;
  }
  if (transaction.amount === null || transaction.feeAmount === null) {
    return null;
  }
  return transaction.amount - transaction.feeAmount;
};

export const RyftNonHostedBalance = () => {
  const { account } = useRyftNonHostedDashboardAccount();

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <BalanceSummary />
      <PendingPayouts key={`pending-${account.ryftAccountId}`} />
      <TransactionHistory key={account.ryftAccountId} />
    </div>
  );
};

const BalanceSummary = () => {
  const query = useRyftManagementLedgerBalance();

  if (query.isPending) {
    return (
      <div
        className="flex min-h-28 items-center justify-center"
        role="status"
        aria-label="Loading balance"
      >
        <Spinner />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Alert type="error" message="Unable to load your balance." />
        <Button color="secondary" onClick={() => query.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const { balance, received, pending, currency } = query.data;
  return (
    <Card className="border border-zinc-200 bg-white">
      <CardContent className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 sm:grid-cols-3 sm:items-center md:px-6">
        <div className="col-span-2 sm:col-span-1">
          <h1 className="text-sm font-medium text-zinc-600">Your balance</h1>
          <p className="mt-1 text-3xl font-semibold text-zinc-900 tabular-nums">
            {formatCurrency(balance, currency)}
          </p>
        </div>
        <dl className="col-span-2 grid grid-cols-2 gap-4 border-t border-zinc-200 pt-4 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-6">
          <div>
            <dt className="flex items-center gap-1.5 text-sm font-medium text-zinc-600">
              Received
              <TooltipContainer content="Money we hold for you. Included in your balance.">
                <button
                  type="button"
                  aria-label="About received funds"
                  className="flex cursor-pointer rounded-sm text-zinc-400 hover:text-zinc-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-600"
                >
                  <Icon icon="INFORMATION_OUTLINE" size={0.6} />
                </button>
              </TooltipContainer>
            </dt>
            <dd className="mt-1 text-xl font-semibold text-zinc-900 tabular-nums">
              {formatCurrency(received, currency)}
            </dd>
          </div>
          <div>
            <dt className="flex items-center gap-1.5 text-sm font-medium text-zinc-600">
              Pending
              <TooltipContainer content="Money on its way to your balance. Included in your balance.">
                <button
                  type="button"
                  aria-label="About pending funds"
                  className="flex cursor-pointer rounded-sm text-zinc-400 hover:text-zinc-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-600"
                >
                  <Icon icon="INFORMATION_OUTLINE" size={0.6} />
                </button>
              </TooltipContainer>
            </dt>
            <dd className="mt-1 text-xl font-semibold text-zinc-900 tabular-nums">
              {formatCurrency(pending, currency)}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
};

const attemptStatusLabels: Record<
  NonNullable<RyftAccountPendingPayoutType["latestAttempt"]>["status"],
  string
> = {
  pending: "Scheduled",
  submitting: "Processing",
  awaiting_confirmation: "Processing",
  uncertain: "Processing",
  succeeded: "Sent to your bank",
  failed: "Failed",
  returned: "Back in your balance",
};

const getPendingPayoutStatusLabel = (payout: RyftAccountPendingPayoutType) => {
  if (payout.latestAttempt === null) {
    return "Scheduled";
  }
  return attemptStatusLabels[payout.latestAttempt.status];
};

const PendingPayouts = () => {
  const query = useRyftManagementPendingPayouts();
  const [selectedPayout, setSelectedPayout] =
    useState<RyftAccountPendingPayoutType | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const selectPayout = (payout: RyftAccountPendingPayoutType) => {
    setSelectedPayout(payout);
    setDetailsOpen(true);
  };
  const closeDetails = () => setDetailsOpen(false);

  const showPendingPayouts = !query.isSuccess || query.data.rows.length > 0;

  const columns: TableSimpleColumn<RyftAccountPendingPayoutType>[] = [
    {
      id: "date",
      header: "Created",
      cell: ({ row }) =>
        dayjs(row.original.createdAt).tz("Europe/London").format("D MMM YYYY"),
    },
    {
      id: "reference",
      header: "Reference",
      cell: ({ row }) => <>Bank payout #{row.original.id}</>,
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => getPendingPayoutStatusLabel(row.original),
    },
    {
      id: "amount",
      header: "Amount",
      cell: ({ row }) => (
        <span className="tabular-nums">
          {formatCurrency(row.original.amount, row.original.currency)}
        </span>
      ),
    },
    {
      id: "details",
      header: "",
      cell: ({ row }) => (
        <Button
          color="slim"
          size="small"
          aria-label={`View bank payout ${row.original.id}`}
          onClick={() => selectPayout(row.original)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <>
      {showPendingPayouts && (
        <section
          className="flex min-w-0 flex-col gap-3"
          aria-labelledby="pending-payouts-heading"
        >
          <h2
            id="pending-payouts-heading"
            className="font-semibold text-zinc-900"
          >
            Pending payouts
          </h2>
          {query.isPending && (
            <div
              className="flex min-h-28 items-center justify-center"
              role="status"
              aria-label="Loading pending payouts"
            >
              <Spinner />
            </div>
          )}
          {query.isError && (
            <div className="flex flex-col items-start gap-3">
              <Alert
                type="error"
                message="Unable to load your pending payouts."
              />
              <Button color="secondary" onClick={() => query.refetch()}>
                Try again
              </Button>
            </div>
          )}
          {query.isSuccess && (
            <TableSimple
              columns={columns}
              data={query.data.rows}
              getRowId={(payout) => payout.id}
              onRowClick={selectPayout}
              gridClasses="grid-cols-[110px_minmax(160px,1fr)_140px_120px_88px]"
              roundedTop
            />
          )}
        </section>
      )}
      <Dialog
        active={detailsOpen}
        onDismiss={closeDetails}
        afterLeave={() => setSelectedPayout(null)}
      >
        <DialogNavigation
          title="Bank payout details"
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={closeDetails}
        />
        <DialogContent className="flex flex-col gap-5">
          {selectedPayout && <PendingPayoutDetails payout={selectedPayout} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

const PendingPayoutDetails = ({
  payout,
}: {
  payout: RyftAccountPendingPayoutType;
}) => {
  const details = [
    { label: "Reference", value: `Bank payout #${payout.id}` },
    { label: "Amount", value: formatCurrency(payout.amount, payout.currency) },
    { label: "Status", value: getPendingPayoutStatusLabel(payout) },
    {
      label: "Created on",
      value: dayjs(payout.createdAt)
        .tz("Europe/London")
        .format("D MMM YYYY, h:mma [UK time]"),
    },
  ];
  const submittedAt = payout.latestAttempt?.submittedAt;
  if (submittedAt != null) {
    details.push({
      label: "Processing started",
      value: dayjs(submittedAt)
        .tz("Europe/London")
        .format("D MMM YYYY, h:mma [UK time]"),
    });
  }
  return (
    <dl className="divide-y divide-zinc-100">
      {details.map(({ label, value }) => (
        <div
          key={label}
          className="grid gap-1 py-3 sm:grid-cols-[160px_1fr] sm:gap-4"
        >
          <dt className="text-sm text-zinc-500">{label}</dt>
          <dd className="min-w-0 text-sm text-zinc-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
};

const TransactionHistory = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [selectedTransactionId, setSelectedTransactionId] = useState<number>();

  const query = useRyftManagementLedgerTransactions({
    offset: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
  });
  const selectedTransaction = query.data?.rows.find(
    (transaction) => transaction.id === selectedTransactionId,
  );
  const selectTransaction = (transaction: Transaction) =>
    setSelectedTransactionId(transaction.id);

  const columnHelper = createSellarColumnHelper<Transaction>();
  const columns = [
    columnHelper.accessor("effectiveAt", {
      header: "Date",
      enableSorting: false,
      cell: (info) =>
        dayjs(info.getValue()).tz("Europe/London").format("D MMM YYYY"),
    }),
    columnHelper.accessor("type", {
      header: "Type",
      enableSorting: false,
      cell: (info) => activityLabels[info.getValue()],
    }),
    columnHelper.accessor((row) => row, {
      id: "reference",
      header: "Reference",
      enableSorting: false,
      cell: (info) => (
        <TransactionReference
          transaction={info.getValue()}
          onSelect={selectTransaction}
        />
      ),
    }),
    columnHelper.accessor("amount", {
      header: "Amount",
      enableSorting: false,
      cell: (info) => {
        const transaction = info.row.original;
        const amount = getAfterFeesAmount(transaction) ?? transaction.amount;
        const amountLabel =
          transaction.type === "capture" && transaction.feeAmount === null
            ? "Fee unavailable"
            : tableAmountLabels[transaction.type];

        if (amount === null) {
          return null;
        }
        return (
          <div>
            <span
              className={twMerge(
                "tabular-nums",
                amount > 0 && "text-green-700",
                amount < 0 && "text-red-700",
              )}
            >
              {amount > 0 && "+"}
              {formatCurrency(amount, transaction.currency)}
            </span>
            {amountLabel && (
              <span className="block text-xs text-zinc-500">{amountLabel}</span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "details",
      header: "",
      enableSorting: false,
      cell: (info) => (
        <Button
          color="slim"
          size="small"
          aria-label={`View transaction ${info.getValue().id}`}
          onClick={() => selectTransaction(info.getValue())}
        >
          View
        </Button>
      ),
    }),
  ];

  return (
    <section
      className="flex min-w-0 flex-col gap-3"
      aria-labelledby="transaction-history-heading"
    >
      <h2
        id="transaction-history-heading"
        className="font-semibold text-zinc-900"
      >
        Transaction history
      </h2>
      {query.isPending && (
        <div
          className="flex min-h-48 items-center justify-center"
          role="status"
          aria-label="Loading transactions"
        >
          <Spinner />
        </div>
      )}
      {query.isError && (
        <div className="flex flex-col items-start gap-3">
          <Alert
            type="error"
            message="Unable to load your transaction history."
          />
          <Button color="secondary" onClick={() => query.refetch()}>
            Try again
          </Button>
        </div>
      )}
      {query.isSuccess && (
        <TablePaginated
          columns={columns}
          data={query.data.rows}
          pageCount={Math.max(
            1,
            Math.ceil(query.data.count / pagination.pageSize),
          )}
          pagination={{ ...pagination, isLoading: query.isFetching }}
          setPagination={setPagination}
          onRowClick={selectTransaction}
          paginationLabel="Transactions per page"
          noResultsText="Your payment activity will appear here."
          gridClasses="grid-cols-[110px_minmax(180px,1fr)_minmax(150px,1fr)_120px_88px]"
          roundedTop
        />
      )}
      <Dialog
        active={!!selectedTransaction}
        onDismiss={() => setSelectedTransactionId(undefined)}
      >
        <DialogNavigation
          title="Transaction details"
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={() => setSelectedTransactionId(undefined)}
        />
        <DialogContent className="flex flex-col gap-5">
          {selectedTransaction && (
            <TransactionDetails transaction={selectedTransaction} />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const TransactionReference = ({
  transaction,
  onSelect,
}: {
  transaction: Transaction;
  onSelect?: (transaction: Transaction) => void;
}) => {
  if (transaction.bankPayoutId != null) {
    return <>Bank payout #{transaction.bankPayoutId}</>;
  }

  if (transaction.orderId !== null) {
    return (
      <ParentLink
        to={ORDER_DETAILS.href({ orderId: transaction.orderId })}
        className="text-sm underline underline-offset-2"
      >
        Order #{transaction.orderId}
      </ParentLink>
    );
  }

  if (transaction.sellarCreditPayoutId !== null) {
    if (onSelect) {
      return (
        <button
          type="button"
          className="text-left text-sm underline underline-offset-2"
          onClick={() => onSelect(transaction)}
        >
          Credit payout #{transaction.sellarCreditPayoutId}
        </button>
      );
    }

    return <>Credit payout #{transaction.sellarCreditPayoutId}</>;
  }
  return <>—</>;
};

const dialogAmountLabels: Record<Transaction["type"], string> = {
  capture: "Original amount",
  refund: "Amount",
  fundsTransfer: "After fees",
  bankPayout: "Amount",
  bankPayoutReversal: "Amount",
};

const formatAmount = (
  amount: number | null,
  currency: Transaction["currency"],
) => {
  if (amount === null) {
    return "Unavailable";
  }
  return formatCurrency(amount, currency);
};

const TransactionDetails = ({ transaction }: { transaction: Transaction }) => {
  const details: { label: string; value: ReactNode }[] = [
    { label: "Type", value: activityLabels[transaction.type] },
    {
      label: "Date",
      value: dayjs(transaction.effectiveAt)
        .tz("Europe/London")
        .format("D MMM YYYY, HH:mm"),
    },
    {
      label: "Reference",
      value: <TransactionReference transaction={transaction} />,
    },
    {
      label: dialogAmountLabels[transaction.type],
      value: formatAmount(transaction.amount, transaction.currency),
    },
  ];
  if (transaction.type === "capture") {
    details.push(
      {
        label: "Fee",
        value: formatAmount(transaction.feeAmount, transaction.currency),
      },
      {
        label: "After fees",
        value: formatAmount(
          getAfterFeesAmount(transaction),
          transaction.currency,
        ),
      },
    );
  }

  return (
    <dl className="divide-y divide-zinc-100">
      {details.map(({ label, value }) => (
        <div
          key={label}
          className="grid gap-1 py-3 sm:grid-cols-[160px_1fr] sm:gap-4"
        >
          <dt className="text-sm text-zinc-500">{label}</dt>
          <dd className="min-w-0 text-sm text-zinc-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
};
